<template>
    <v-row>
      <v-dialog v-model="showDialog" scrollable max-width="1140px">
         <v-skeleton-loader
            v-if="loading"
            type="article, actions">
         </v-skeleton-loader>
            <v-card v-if="!loading">
               <v-card-title>
                  <v-panel>
                     <span style="font-size: 20px;">
                        {{ !data.id ? $t('wkt.admin.workplace.edit-window.new_workplace_title') : $t('wkt.admin.workplace.edit-window.edit_workplace_title' )}}
                     </span>
                  </v-panel>
               </v-card-title>
               <v-card-text>
                  <v-container>
                     <v-form ref="form">
                        <v-row>
                           <v-col cols="6">                
                              <v-text-field
                                 v-model="data.name"
                                 :label="$t('wkt.admin.workplace.edit-window.name')"
                                 autocomplete="none"
                                 :rules="[validationRules.required]"
                                 max="200" 
                              />
                           </v-col>
                           <v-col cols="4" class="pb-0 d-flex justify-end">  
                              <v-switch
                                 v-model="data.geofenceEnabled"
                                 :label="$t('wkt.admin.workplace.edit-window.enable_geolocation')"
                                 @change="updateGeofenceRadiusStatus($event)"
                              />
                           </v-col>
                           <v-col cols="6">                
                              <v-text-field
                                 v-model="data.geofenceRadius"
                                 :disabled="!geofenceRadiusEnabled"
                                 :label="$t('wkt.admin.workplace.edit-window.geolocation_radius')"
                                 autocomplete="none"
                                 :rules="[
                                    ...(geofenceRadiusEnabled ? [validationRules.required, validationRules.rangeInt(data.geofenceRadius, 10, 9999999)] : [])
                                 ]"
                                 type="number"
                                 min="10"
                                 max="9999999"
                              />
                            </v-col>
                           <v-col cols="6">                
                              <v-select
                                 v-model="data.countryId"
                                 :items="countries"
                                 :item-text="item => $t(item.name)"
                                 item-value="id"
                                 menu-props="bottom, offsetY"
                                 :label="$t('wkt.admin.workplace.edit-window.country')"
                                 :rules="[validationRules.required]"
                                 @change="updateGmapAutocompleteStatus($event)"
                              />
                           </v-col>
                           <v-col cols="12" class="mb-8">
                              <span class="d-block">{{ $t('wkt.admin.workplace.edit-window.address') }}</span>  
                              <!-- Añadimos un key dinámico para forzar la actualización cuando se cambian las opciones -->
                              <google-map-autocomplete
                                 ref="gmapAutocomplete"
                                 class="mb-2 workplace-lookup"
                                 :key="gmapAutocompleteKey"
                                 :options="options"
                                 :controls="false"
                                 :types="['address']"
                                 :placeholder="$t('wkt.admin.workplace.edit-window.address')"
                                 @ready="gMapAutocompleteReady"
                                 @place-changed="getGMapAutocompleteAddressData"
                                 :rules="[validationRules.required]"
                              />
                              <span class="error--text" :style="'height: 16px; visibility: ' + (gmapRequired ? 'visible' : 'hidden') + ';'">
                                 {{ $t('wkt.admin.common.validation-utils.required_field') }}
                              </span>
                           </v-col>
                        </v-row>
                     </v-form>
                  </v-container>
               </v-card-text>
               <v-divider></v-divider>
               <v-card-actions>
               <v-btn v-if="data.id" color="#ff0000" @click="confirmDelete()">
                  <v-icon left color="#ffffff">delete</v-icon>
                  <span style="color: #ffffff;">{{ $t('wkt.admin.common.dialog-buttons.delete').toUpperCase() }}</span>
               </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn color="#185EA9" text @click="onSave()" style="font-size: 0.9em; font-weight: 400;">{{ $t('wkt.admin.common.dialog-buttons.ok').toUpperCase() }}</v-btn>
                  <v-btn text @click="showDialog=false" style="font-size: 0.9em; font-weight: 400;">{{ $t('wkt.admin.common.dialog-buttons.cancel').toUpperCase() }}</v-btn>
               </v-card-actions>
            </v-card>
      </v-dialog> 
      <ConfirmDialog ref="dlgConfirm"
         v-bind:title="$t('wkt.admin.workplace.edit-holidays-window.confirm-dialog.title')"
         v-bind:message="$t('wkt.admin.workplace.edit-holidays-window.confirm-dialog.text')"
         v-bind:titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
         v-bind:titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
         v-on:confirmed="deleteItem()"
      />
   </v-row>	
</template>
  
<script>
import BaseModule from '../../common/BaseModule.js';
import ConfirmDialog from '../../common/ConfirmDialog.vue';
import api from '../../../api/worktime.js';
import gwt from '../../../api/gwt.js';

export default {
   name: 'WorkplaceDialog',
   mixins: [BaseModule],
   components: {
      ConfirmDialog
   },
   data() {
      return {
         showDialog: false,
         loading: false,
         data: {},
         gmapAutocompleteKey: 0,
         countries: [],
         options: {
            fields: ["address_components", "formatted_address", "geometry"]
         },
         geofenceRadiusEnabled: false,
         isGMapAutocompleteReady: false,
         gmapRequired: false
      }
   },
   computed: {
      isGmapAutocompleteValueEmpty() {         
         if (!this.data.geofenceAddress || this.data.geofenceAddress.trim().length === 0) {
            return true;
         }

         if (!this.$refs.gmapAutocomplete.$refs.input.value || this.$refs.gmapAutocomplete.$refs.input.value.trim().length === 0) {
            return true;
         }

         return false;
      }
   },
   methods: {
      // eslint-disable-next-line no-unused-vars
      open(action, payload) {
         this.geofenceRadiusEnabled = false;
         this.gmapRequired = false;
         this.showDialog = true;
         if (this.$refs.form) {
            this.$refs.form.resetValidation();
         }

         this.countries = gwt.getSessionInformation().countries;

         const myCountryId = gwt.getSessionInformation().company.countryId;

         this.updateGmapAutocompleteStatus(myCountryId);
 
         // Estamos creando un elemento
         if (!payload) {
            this.data = {
               companyId: gwt.getSessionProfile().companyId,               
               countryId: this.countries[0].id,
               geofenceAddress: '',
               geofenceRadius: 25.0
            };
            return;
         }

         this.loading = true;

         api.getWorkplaceById(payload, 
            (res) => {
               this.loading = false;
               this.data = res;
               
               this.geofenceRadiusEnabled = this.data.geofenceEnabled;
               this.data.geofenceLatitude = this.data.geofenceLatitude ? this.data.geofenceLatitude : 0.0;
               this.data.geofenceLongitude = this.data.geofenceLongitude ? this.data.geofenceLongitude : 0.0;
            }, 
            (error) => {
               this.$emit('show-alert', { text: error, color: 'error', timeout: 3000 });
               this.loading = false;
            }
         );
      },
      updateGeofenceRadiusStatus(enabled) {
         this.geofenceRadiusEnabled = enabled;
      },
      updateGmapAutocompleteStatus(countryId) {
         const myCountry = this.countries.find(x => x.id === countryId);

         if (!myCountry) {
            return;
         }                  

         this.options.componentRestrictions = { 
            country: myCountry.iso3166Alpha2Code
         };

         this.gmapAutocompleteKey++;

         if (this.isGMapAutocompleteReady) {
            this.$refs.gmapAutocomplete.$refs.input.value = '';
            this.data.geofenceAddress = '';
         }
      },
      gMapAutocompleteReady() {
         this.isGMapAutocompleteReady = true;

         if (!this.data || !this.data.geofenceAddress) {
            this.$refs.gmapAutocomplete.$refs.input.value = '';
            return;
         }

         this.$refs.gmapAutocomplete.$refs.input.value = this.data.geofenceAddress;
      },
      getGMapAutocompleteAddressData(data) {
         this.data.geofenceAddress = data.formatted_address;
         this.data.geofenceLatitude = data.geometry.location.lat();
         this.data.geofenceLongitude = data.geometry.location.lng();
      },
      getReferencingCalendars() {
         if (!this.data || !this.data.id) {
            return [];
         }

         return gwt.getCalendars().filter(calendar => calendar.geolocationWorkplaceId === this.data.id);
      },
      validateReferencedGeofence() {
         const referencingCalendars = this.getReferencingCalendars();
         if (!referencingCalendars.length) {
            return true;
         }

         const hasValidGeofence = this.data.geofenceEnabled
            && this.data.geofenceAddress
            && this.data.geofenceLatitude != null
            && this.data.geofenceLongitude != null
            && this.data.geofenceRadius != null;

         if (hasValidGeofence) {
            return true;
         }

         const calendarNames = referencingCalendars.map(calendar => calendar.name).join(', ');
         let message = this.$t('wkt.admin.workplace.edit-window.referenced_geofence_error');
         message = message.replace('%s', calendarNames);
         this.$emit('show-alert', { text: message, color: 'error', timeout: 6000 });
         return false;
      },
      onSave() {
         const isFormValid = this.$refs.form.validate();

         const gmapEmpty = this.isGmapAutocompleteValueEmpty;

         this.gmapRequired = gmapEmpty;

         // Si hay algún campo no válido, no continuamos
         if (!isFormValid || gmapEmpty) {
            return;
         }

         if (!this.validateReferencedGeofence()) {
            return;
         }

         this.loading = true;

         api.storeWorkplace(this.data,
            () => {
               this.loading = false;
               this.showDialog = false;
               this.$emit('refresh');
            },
            (error) => {
               this.loading = false;
               this.$log.error(error);
               this.$emit('show-alert', { text: error, color: 'error', timeout: 3000 });
            }
         );
      },
      confirmDelete() {
         api.getEmployeeCountByWorkPlace(this.data.id,
            (res) => {
               if (res > 0) {
                  let message = this.$t('wkt.admin.workplace.btn-delete-error');
                  message = message.replace('%d', `${res}`);
                  this.$emit('show-alert', { text: message, color: 'error', timeout: 6000 });
                  return;
               } 
               
               this.$refs.dlgConfirm.confirm(this.data);
            },
            (error) => {
               this.$log.error(error);
               this.$emit('show-alert', { text: error, color: 'error', timeout: 3000 });
            }
         );
      },
      deleteItem() {
      const referencingCalendars = this.getReferencingCalendars();
      if (referencingCalendars.length) {
         const calendarNames = referencingCalendars.map(calendar => calendar.name).join(', ');
         let message = this.$t('wkt.admin.workplace.edit-window.referenced_delete_error');
         message = message.replace('%s', calendarNames);
         this.$emit('show-alert', { text: message, color: 'error', timeout: 6000 });
         return;
      }

        this.loading = true;

        api.deleteWorkplace(this.data.id, 
            () => {
               this.loading = false;
               this.showDialog = false;
               this.$emit('refresh');
            }, 
           (error) => {
               this.loading = false;
               this.$log.error(error);
               this.$emit('show-alert', { text: error, color: 'error', timeout: 3000 });
            }
         );
      },
   }
}
</script>